import React, { useState, useEffect, useCallback } from 'react';
import { formatNumber } from '../services/utils';
import { fetchMarkups, saveMarkups, refreshMarkups } from '../services/markupsAPI';

const CURRENCY_NAMES = {
  AED: 'United Arab Emirates Dirham',
  AUD: 'Australian Dollar',
  BGN: 'Bulgarian Lev',
  BRL: 'Brazilian Real',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan Renminbi',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  EUR: 'Euro',
  GBP: 'British Pound Sterling',
  HKD: 'Hong Kong Dollar',
  HRK: 'Croatian Kuna',
  HUF: 'Hungarian Forint',
  IDR: 'Indonesian Rupiah',
  ILS: 'Israeli New Shekel',
  INR: 'Indian Rupee',
  ISK: 'Icelandic Króna',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  LTL: 'Lithuanian Litas',
  MAD: 'Moroccan Dirham',
  MXN: 'Mexican Peso',
  MYR: 'Malaysian Ringgit',
  NOK: 'Norwegian Krone',
  NZD: 'New Zealand Dollar',
  OMR: 'Omani Rial',
  PHP: 'Philippine Peso',
  PLN: 'Polish Złoty',
  RON: 'Romanian Leu',
  RUB: 'Russian Ruble',
  SCR: 'Seychellois Rupee',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  TRY: 'Turkish Lira',
  USD: 'United States Dollar',
  XDR: 'Special Drawing Rights (IMF)',
  ZAR: 'South African Rand',
};

function CurrencyAdmin() {
  const [rows, setRows] = useState([]);
  const [ratesFetchedAt, setRatesFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMarkups();
      setRows(data.markups || []);
      setRatesFetchedAt(data.rates_fetched_at || null);
      setDirty(false);
    } catch (err) {
      console.error('Failed to load markups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkupChange = (currency, rawValue) => {
    const normalized = String(rawValue).replace(',', '.');
    setRows(prev => prev.map(r => {
      if (r.currency !== currency) return r;
      const pct = parseFloat(normalized);
      const markup_pct = Number.isFinite(pct) ? pct : 0;
      const calculated_rate = r.current_rate * (1 + markup_pct / 100);
      const buy_rate = markup_pct ? r.current_rate / (1 + markup_pct / 100) : r.current_rate;
      return { ...r, markup_pct, calculated_rate: Math.round(calculated_rate * 1000000) / 1000000, buy_rate: Math.round(buy_rate * 1000000) / 1000000, _raw: rawValue };
    }));
    setDirty(true);
    setSaveMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const entries = rows.map(r => ({ currency: r.currency, markup_pct: r.markup_pct }));
      const data = await saveMarkups(entries);
      setRows(data.markups || rows);
      setDirty(false);
      setSaveMessage('Saved successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save markups:', err);
      setSaveMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setSaveMessage('');
    try {
      const data = await refreshMarkups();
      setRows(data.markups || []);
      setRatesFetchedAt(data.rates_fetched_at || null);
      setDirty(false);
      setSaveMessage('Rates refreshed from Nationalbanken and Frankfurter.dev');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Failed to refresh rates:', err);
      setSaveMessage('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const fmtDate = ratesFetchedAt
    ? new Date(ratesFetchedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

  return (
    <div className="min-h-screen bg-white px-6 py-8 font-sans">
        <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-black/90">Currency Markup Admin</h1>
            <p className="mt-1 text-xs text-black/50">
              Set a global markup % for each currency. Rates from Nationalbanken are refreshed daily.
            </p>
            <p className="mt-1 text-xs text-black/40">
              Last rate update: <span className="font-medium text-black/60">{fmtDate}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-lg border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-black/70 transition hover:border-company-main/40 hover:text-company-main disabled:opacity-50"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Rates'}
            </button>

            <button
              type="button"
              className="rounded-lg bg-company-main px-4 py-2 text-xs font-semibold text-white shadow-lg transition hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {saveMessage && (
          <div className={`mb-4 rounded-lg px-4 py-2 text-xs font-semibold ${saveMessage.includes('fail') ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
            {saveMessage}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-sm text-black/40">Loading currencies...</div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-sm text-black/40">
            No currencies found. Click <strong>Refresh Rates</strong> to fetch from Nationalbanken.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-black/10 shadow-sm">
            <table className="w-full table-fixed border-collapse text-xs">
              <thead>
                <tr className="border-b border-black/10 bg-company-toned">
                  <th className="w-[100px] px-4 py-3 text-left font-semibold text-black/80">Currency</th>
                  <th className="w-[140px] px-4 py-3 text-right font-semibold text-black/80">Current Rate</th>
                  <th className="w-[120px] px-4 py-3 text-right font-semibold text-black/80">Markup (%)</th>
                  <th className="w-[140px] px-4 py-3 text-right font-semibold text-black/80">Sell Rate</th>
                  <th className="w-[140px] px-4 py-3 text-right font-semibold text-black/80">Buy Rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.currency} className={idx % 2 === 1 ? 'bg-company-toned/40' : 'bg-white'}>
                    <td className="border-b border-black/5 px-4 py-2.5 font-semibold text-black/80">
                      <span
                        title={CURRENCY_NAMES[row.currency] || row.currency}
                        className="cursor-help underline decoration-dotted decoration-black/30 underline-offset-4"
                      >
                        {row.currency}
                      </span>
                    </td>
                    <td className="border-b border-black/5 px-4 py-2.5 text-right text-black/70">
                      {formatNumber(row.current_rate/100, 4)}
                    </td>
                    <td className="border-b border-black/5 px-4 py-2.5 text-right">
                      <input
                        type="text"
                        className="w-[100px] rounded-md border border-company-main/30 bg-white px-2 py-1 text-right text-xs text-black/80 focus:border-company-main/70 focus:outline-none focus:ring-2 focus:ring-company-main/15"
                        value={row._raw !== undefined ? row._raw : formatNumber(row.markup_pct, 2)}
                        onChange={(e) => handleMarkupChange(row.currency, e.target.value)}
                        onFocus={(e) => {
                          const val = row.markup_pct;
                          handleMarkupChange(row.currency, val === 0 ? '' : String(val));
                        }}
                        onBlur={() => {
                          setRows(prev => prev.map(r =>
                            r.currency === row.currency ? { ...r, _raw: undefined } : r
                          ));
                        }}
                        inputMode="decimal"
                        autoComplete="off"
                      />
                    </td>
                    <td className="border-b border-black/5 px-4 py-2.5 text-right font-semibold text-black/80">
                      {formatNumber(row.calculated_rate/100, 4)}
                    </td>
                    <td className="border-b border-black/5 px-4 py-2.5 text-right font-semibold text-black/80">
                      {formatNumber(row.buy_rate/100, 4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6">
          <a
            href={window.location.pathname.includes('/admin') ? '/' + window.location.search : '/admin' + window.location.search}
            className="text-xs font-medium text-company-main underline hover:text-company-main/80"
          >
            {window.location.pathname.includes('/admin') ? '← Back to Offer Builder' : 'Currency Admin →'}
          </a>
        </div>
      </div>
    </div>
  );
}

export default CurrencyAdmin;
